import { Box, Button, IconButton, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import { ArrowLeftIcon, ArrowRightIcon } from "@mui/x-date-pickers";
import clsx from "clsx";
import * as React from "react";
import { FormEvent } from "react";
import LoginPanel from "../containers/LoginPanel";
import { Form, KeyValue } from "../interfaces";
const PREFIX = "SearchForm";

const classes = {
  findBankButton: `${PREFIX}-findBankButton`,
  formStyles: `${PREFIX}-formStyles`,
  showPanelButton: `${PREFIX}-showPanelButton`,
  hidePanelButton: `${PREFIX}-hidePanelButton`,
  searchButton: `${PREFIX}-searchButton`,
  loginButton: `${PREFIX}-loginButton`,
};

const Root = styled("div")(({ theme }) => ({
  [`& .${classes.findBankButton}`]: {
    "&.MuiButton-contained.MuiButton-colorSecondary": {
      margin: "10px auto",
      display: "block",
      padding: "0px 24px",
    },
    "&.MuiButton-contained.MuiButton-colorPrimary": {
      "width": 160,
      "fontSize": "1rem",
      "height": 40,
      "display": "block",
      "margin": "0 auto",
      "&:hover, &.Mui-focusVisible": {
        color: "#1A4480",
        backgroundColor: "#fff",
        border: "1px solid #ACC8DF",
      },
      "&:active": {
        backgroundColor: "#7B97BC",
        borderColor: "#1A4480",
        color: "#fff",
      },
    },
  },

  [`& .${classes.formStyles}`]: {
    "width": "100%",
    "paddingLeft": "1rem",
    "paddingRight": "1rem",
    "& ul": {
      padding: 0,
      margin: 0,
    },
    "& li": {
      "padding": "0",
      "display": "block",
      "& h2": {
        paddingBottom: 0,
      },

      "& h3, & h4": { marginBottom: "1rem", paddingBottom: 0 },
      "& .MuiFormControl-marginDense": {
        marginBottom: "1rem",
        margin: 0,
      },
    },
  },

  [`& .${classes.showPanelButton}`]: {
    "borderRadius": 4,
    "backgroundColor": theme.palette.secondary.main,
    "color": "#1A4480",
    "border": "1px solid #acc8df",
    "transition": "none",
    "height": 40,
    "width": 40,
    "padding": 0,
    "&:hover, &:focus": {
      color: "#ffffff",
      backgroundColor: "#1A4480",
    },
    "&:active": {
      color: "#1A4480",
      backgroundColor: "#ffffff",
    },
  },

  [`& .${classes.hidePanelButton}`]: {
    "marginTop": 0,
    "marginBottom": ".5rem",
    "width": "100%",
    "textTransform": "capitalize",
    "fontSize": 14,
    "fontWeight": 400,
    "color": "#1A4480",
    "borderBottomRightRadius": 0,
    "borderBottomLeftRadius": 0,
    "transition": "none",
    "borderBottom": "1px solid #acc8df",
    "&:hover, &:focus": {
      color: "#ffffff",
      backgroundColor: "#1A4480",
    },
    "&:active": {
      color: "#1A4480",
      backgroundColor: "#ffffff",
    },
  },

  [`& .${classes.searchButton}`]: {
    "paddingTop": "1.5rem",
    "paddingBottom": "1.5rem",
    "& button.MuiButton-containedSecondary": {
      "margin": "10px auto",
      "display": "block",
      "padding": "0px 24px",
      "backgroundColor": "#1a4480",
      "color": "white",
      "&:hover, &.Mui-focusVisible": {
        color: "#1A4480",
        backgroundColor: "#fff",
        border: "1px solid #ACC8DF",
      },
    },
    "& button.MuiButton-containedPrimary": {
      "width": 160,
      "fontSize": "1rem",
      "height": 40,
      "display": "block",
      "margin": "0px auto",
      "&:hover, &.Mui-focusVisible": {
        color: "#1A4480",
        backgroundColor: "#fff",
        border: "1px solid #ACC8DF",
      },
      "&:active": {
        backgroundColor: "#7B97BC",
        borderColor: "#1A4480",
        color: "#fff",
      },
    },
  },

  [`& .${classes.loginButton}`]: {
    "paddingTop": 0,
    "& button": { height: 40, padding: 7, fontSize: 16 },
  },
}));

export function SearchForm({
  formList,
  form,
  onSubmit,
  searchPanelExpand,
  setSearchPanelExpand,
  canHideSearchPanel,
  disabled,
  prevForm,
  setPrevForm,
  header,
  searchButtonText,
  secondaryButton,
  externalSubmit,
  noChangeSubmit,
  hideSearchButton,
  login,
  scroll,
  optionalIndicator,
  onReset,
  displayReset,
}: {
  formList: React.ReactElement;
  form: Form;
  onSubmit: (form: Form, submitted?: boolean) => void;
  searchPanelExpand: boolean;
  setSearchPanelExpand: (updatedSearchPanelExpand: boolean) => void;
  canHideSearchPanel: boolean;
  prevForm: KeyValue;
  setPrevForm: (prevForm: KeyValue) => void;
  header?: string;
  disabled?: boolean;
  externalSubmit?: boolean;
  searchButtonText?: string;
  secondaryButton?: boolean;
  noChangeSubmit?: boolean;
  hideSearchButton?: boolean;
  login?: boolean;
  scroll?: boolean;
  optionalIndicator?: boolean;
  onReset?: () => void;
  displayReset?: boolean;
}) {
  function submitForm(e: FormEvent) {
    e.preventDefault();
    if (JSON.stringify(prevForm) !== JSON.stringify(form) || noChangeSubmit) {
      setPrevForm(form);
      onSubmit(form, noChangeSubmit);
    }
    if (scroll) {
      window.scrollTo(0, 150);
    }
  }

  React.useEffect(() => {
    if (externalSubmit) {
      if (JSON.stringify(prevForm) !== JSON.stringify(form)) {
        setPrevForm(form);
        onSubmit(form);
      }
    }
  });

  const handleCollapse = () => {
    setSearchPanelExpand(!searchPanelExpand);
  };

  const formContainer = (children: React.ReactElement) =>
    hideSearchButton ? (
      <div className={classes.formStyles}>{children}</div>
    ) : (
      <form
        id="search-panel-form"
        onSubmit={submitForm}
        className={classes.formStyles}
        noValidate
        autoComplete="off"
      >
        {children}

        <div
          className={clsx(classes.searchButton, login && classes.loginButton)}
          id="search-form-submit"
        >
          {!login ? (
            <>
              {optionalIndicator && (
                <Typography
                  variant="body2"
                  sx={{
                    color: "#1a4480",
                    textAlign: "center",
                  }}
                >
                  All fields are optional.
                </Typography>
              )}
              {displayReset ? (
                <Box
                  sx={{
                    display: "flex",
                    gap: 2,
                  }}
                >
                  <Button
                    id="resetSearch"
                    disableElevation
                    variant="contained"
                    disabled={disabled}
                    sx={{
                      backgroundColor: "#ACC8DF",
                      color: "#1A4480",
                      border: "1px solid #1A4480",
                    }}
                    onClick={() => {
                      onReset && onReset();
                    }}
                  >
                    Reset
                  </Button>

                  <Button
                    id="findBankSearch"
                    disableElevation
                    color={secondaryButton ? "secondary" : "primary"}
                    variant="contained"
                    type="submit"
                    disabled={disabled}
                    sx={{ padding: 0 }}
                  >
                    {searchButtonText || "Search"}
                  </Button>
                </Box>
              ) : (
                <Button
                  id="findBankSearch"
                  disableElevation
                  color={secondaryButton ? "secondary" : "primary"}
                  variant="contained"
                  type="submit"
                  disabled={disabled}
                >
                  {searchButtonText || "Search"}
                </Button>
              )}
            </>
          ) : (
            <LoginPanel formButton label="Continue via Login.gov" />
          )}
        </div>
      </form>
    );
  return (
    <Root>
      {searchPanelExpand ? (
        <>
          {header && (
            <Typography style={{ whiteSpace: "nowrap" }} variant="h2">
              {header}
            </Typography>
          )}
          <Box
            id="search-form-box"
            sx={{
              bgcolor: "secondary.main",
              borderRadius: "5px",
              justifyContent: "center",
              border: "1px solid #ACC8DF",
            }}
          >
            {canHideSearchPanel ? (
              <Button
                id="hide-search-panel-button"
                className={classes.hidePanelButton}
                onClick={handleCollapse}
                startIcon={<ArrowLeftIcon />}
              >
                Hide search panel
              </Button>
            ) : undefined}
            {formContainer(formList)}
          </Box>
        </>
      ) : (
        <IconButton
          id="show-search-panel-button"
          className={classes.showPanelButton}
          onClick={handleCollapse}
          size="large"
        >
          <ArrowRightIcon />
        </IconButton>
      )}
    </Root>
  );
}
